import React from "react";

type MemoizedGaugeChartProps = {
  percent: number;
  id: string;
  nrOfLevels?: number;
  colors?: string[];
  arcPadding?: number;
  style?: React.CSSProperties;
};

const MemoizedGaugeChartNewUI: React.FC<MemoizedGaugeChartProps> = React.memo(
  ({
    percent,
    id,
    nrOfLevels = 10,
    colors = ["#FF5F6D", "#FFC371", "#00b300"],
    arcPadding = 0.08,
    style = { width: "80px", height: "40px" },
  }) => {
    const clamped = Math.max(0, Math.min(1, percent));
    const levels = nrOfLevels;
    const angleStep = 180 / (levels - 1);
    const radius = 40;
    const centerX = 50;
    const centerY = 50;
    const needleLength = 30;
    const needleThickness = 4; // constant width

    // smooth gradient between colors
    const getColor = (index: number) => {
      const ratio = index / (levels - 1);
      if (ratio <= 0.5) {
        const local = ratio / 0.5;
        return lerpColor(colors[0], colors[1], local);
      } else {
        const local = (ratio - 0.5) / 0.5;
        return lerpColor(colors[1], colors[2], local);
      }
    };

    const lerpColor = (a: string, b: string, t: number): string => {
      const ah = parseInt(a.replace("#", ""), 16);
      const bh = parseInt(b.replace("#", ""), 16);
      const ar = (ah >> 16) & 255,
        ag = (ah >> 8) & 255,
        ab = ah & 255;
      const br = (bh >> 16) & 255,
        bg = (bh >> 8) & 255,
        bb = bh & 255;
      const rr = Math.round(ar + (br - ar) * t);
      const rg = Math.round(ag + (bg - ag) * t);
      const rb = Math.round(ab + (bb - ab) * t);
      return `#${((1 << 24) + (rr << 16) + (rg << 8) + rb)
        .toString(16)
        .slice(1)}`;
    };

    const dots = Array.from({ length: levels }, (_, i) => {
      const angle = 180 - i * angleStep;
      const rad = (angle * Math.PI) / 180;
      const x = centerX + radius * Math.cos(rad);
      const y = centerY - radius * Math.sin(rad);
      return { x, y, color: getColor(i) };
    });

    // needle geometry
    const needleAngle = 180 - clamped * 180;
    const needleRad = (needleAngle * Math.PI) / 180;
    const dirX = Math.cos(needleRad);
    const dirY = -Math.sin(needleRad);
    const perpX = -dirY;
    const perpY = dirX;

    const base1X = centerX + perpX * (needleThickness / 2);
    const base1Y = centerY + perpY * (needleThickness / 2);
    const base2X = centerX - perpX * (needleThickness / 2);
    const base2Y = centerY - perpY * (needleThickness / 2);
    const tipX = centerX + dirX * needleLength;
    const tipY = centerY + dirY * needleLength;

    return (
      <div style={{ ...style, position: "relative" }}>
        <svg viewBox="0 0 100 60" style={{ width: "100%", height: "100%" }}>
          {/* Base arc track */}
          <path
            d="M10,50 A40,40 0 0,1 90,50"
            fill="none"
            stroke="#f1f1f1"
            strokeWidth="8"
          />

          {/*  dots */}
          {dots.map((d, i) => (
            <circle key={i} cx={d.x} cy={d.y} r={5.8} fill={d.color} />
          ))}

          {/* Needle */}
          <path
            d={`M ${base1X},${base1Y} L ${tipX},${tipY} L ${base2X},${base2Y} Z`}
            fill="#464A4F"
          />

          {/* Center dot */}
          <circle cx={centerX} cy={centerY} r={2.3} fill="#464A4F" />
        </svg>
      </div>
    );
  }
);

MemoizedGaugeChartNewUI.displayName = "MemoizedGaugeChartNewUI";
export default MemoizedGaugeChartNewUI;
